import React from 'react'
import { Col, Divider, Form, Input, InputNumber, Row, Switch } from 'antd'
import { useTranslation } from 'react-i18next'
import { Modal } from '~/Components/Modal'
import { Client } from '~/Services/Clients'

export type ClientFormValues = {
  clientId: string
  clientName: string
  allowOfflineAccess: boolean
  allowAccessTokensViaBrowser: boolean
  updateAccessTokenClaimsOnRefresh: boolean
  refreshTokenExpiration: number
  enabled: boolean
  requireClientSecret: boolean
}

export const DEFAULT_FORM_VALUES: ClientFormValues = {
  clientId: '',
  clientName: '',
  allowOfflineAccess: true,
  allowAccessTokensViaBrowser: true,
  updateAccessTokenClaimsOnRefresh: true,
  refreshTokenExpiration: 0,
  enabled: true,
  requireClientSecret: false,
}

interface ClientFormModalProps {
  editingClient: Client | null
  visible: boolean
  submitting: boolean
  form: any
  onCancel: () => void
  onSubmit: () => void
  onFinish: (values: ClientFormValues) => void
}

export const ClientFormModal = ({
  editingClient,
  visible,
  submitting,
  form,
  onCancel,
  onSubmit,
  onFinish,
}: ClientFormModalProps) => {
  const { t } = useTranslation()

  return (
    <Modal
      title={editingClient ? t('admin.updateClient') : t('admin.createClient')}
      visible={visible}
      onCancel={onCancel}
      onOk={onSubmit}
      confirmLoading={submitting}
      width={800}
      destroyOnClose
      okText={t('admin.save')}
      cancelText={t('admin.cancel')}
      loadingData={submitting}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={DEFAULT_FORM_VALUES}
        onFinish={onFinish}
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t('admin.clientId')}
              name="clientId"
              rules={[{ required: true, message: t('admin.clientIdRequired') }]}
            >
              <Input disabled={!!editingClient} placeholder={t('admin.enterClientId')} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t('admin.clientName')}
              name="clientName"
              rules={[{ required: true, message: t('admin.clientNameRequired') }]}
            >
              <Input placeholder={t('admin.enterClientName')} />
            </Form.Item>
          </Col>
        </Row>

        {editingClient && (
          <>
            <Row gutter={16}>
              <Col xs={24} md={24}>
                <Form.Item
                  label={t('admin.refreshTokenExpiration')}
                  name="refreshTokenExpiration"
                  rules={[
                    {
                      required: true,
                      message: t('admin.refreshTokenExpirationRequired'),
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder={t('admin.enterRefreshTokenExpiration')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={24}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: '8px 16px',
                  }}
                >
                  <Form.Item
                    label={t('admin.enabled')}
                    name="enabled"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    label={t('admin.requireClientSecret')}
                    name="requireClientSecret"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    label={t('admin.allowOfflineAccess')}
                    name="allowOfflineAccess"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    label={t('admin.allowAccessTokensViaBrowser')}
                    name="allowAccessTokensViaBrowser"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    label={t('admin.updateAccessTokenClaimsOnRefresh')}
                    name="updateAccessTokenClaimsOnRefresh"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Modal>
  )
}

